import CompactImage from './CompactImage.js';
import * as Edit from './ImageDataEdit.js';
import PenDrawableImage from './PenDrawableImage.js';
import SelectionProcessor from './SelectionProcessor.js';

export default class ImageDataManager {
  static canvasBackground = null;
  static frontLayerOpacity = .5;
  static backLayerOpacity = .5;
  static frontFrameOpacity = .5;
  static backFrameOpacity = .5;
  static _selectionTransformAlgo = 'Auto';
  static _pseudoClipboard = null;
  static _lastCopiedRect = null;

  constructor(onResize, updatePreview, updateLayerThumbnail, updateFrameThumbnail) {
    this._onResize = onResize;
    this._updatePreview = updatePreview;
    this._updateLayerThumbnail = updateLayerThumbnail;
    this._updateFrameThumbnail = updateFrameThumbnail;
    this._width = 64;
    this._height = 64;
    this._frameDataList = [];
    this._pending = null;
    this._pendingTarget = 0;// target=0:中央のみ,1:選択フレーム,2:全フレーム
    this._initSelection();
    this._layerIdx = 0;
    this._frameIdx = 0;
    this._frontLayerCtx = new OffscreenCanvas(0, 0).getContext('2d');
    this._backLayerCtx = new OffscreenCanvas(0, 0).getContext('2d');
    this._frontFrameCtx = new OffscreenCanvas(0, 0).getContext('2d');
    this._backFrameCtx = new OffscreenCanvas(0, 0).getContext('2d');
    this._tmpCtx = new OffscreenCanvas(0, 0).getContext('2d', {willReadFrequently: true});
    this._resizeCanvases(this._width, this._height);
  }

  copy(src) {
    this._width = src._width;
    this._height = src._height;
    for(let i = 0; i < src._frameDataList.length; ++i) {
      this._frameDataList.push(src._cloneFrameData(i));
    }
    this._resizeCanvases(this._width, this._height);
  }

  /*-------------------------------- 画像サイズ --------------------------------*/
  get width() { return this._width; }
  get height() { return this._height; }
  get clipboardPermitted() { return navigator.clipboard.read != undefined && navigator.clipboard.write != undefined && window == window.parent; }

  //--- x,yが画像サイズ内か
  withinImage(x, y) {
    return 0 <= x && x < this.width && 0 <= y && y < this.height;
  }

  //--- 全レイヤー共通のキャンバスサイズを変更
  resize(x1, y1, x2, y2) {
    const paramsList = [];
    const undoArgs = [-x1, -y1, -x1 + this._width - 1, -y1 + this._height - 1, paramsList];
    const [width, height] = [x2 - x1 + 1, y2 - y1 + 1];
    this._width = width;
    this._height = height;
    let byteSizeSum = 0;
    this._frameDataList.forEach((frameData, frameIdx) => {
      frameData.layerDataList.forEach((layerData, layerIdx) => {
        const params = layerData.compactImage.resize(x1, y1, x2, y2);
        byteSizeSum += params.byteSize;
        delete params.byteSize;
        paramsList.push(params);
        this._onUpdateLayer(frameIdx, layerIdx);
      });
      this._onUpdateFrame(frameIdx);
    });
    this._resizeCanvases(width, height);
    this._onResize();
    return [undoArgs, byteSizeSum];
  }
  undoResize(x1, y1, x2, y2, paramsList) {
    const [width, height] = [x2 - x1 + 1, y2 - y1 + 1];
    this._width = width;
    this._height = height;
    paramsList = paramsList.slice();
    this._frameDataList.forEach((frameData, frameIdx) => {
      frameData.layerDataList.forEach((layerData, layerIdx) => {
        layerData.compactImage.undoResize(paramsList.shift());
        this._onUpdateLayer(frameIdx, layerIdx);
      });
      this._onUpdateFrame(frameIdx);
    });
    console.assert(paramsList.length === 0);
    this._resizeCanvases(x2 - x1 + 1, y2 - y1 + 1);
    this._onResize();
  }
  _resizeCanvases(width, height) {
    this._frontLayerCtx.canvas.width = width;
    this._frontLayerCtx.canvas.height = height;
    this._backLayerCtx.canvas.width = width;
    this._backLayerCtx.canvas.height = height;
    this._frontFrameCtx.canvas.width = width;
    this._frontFrameCtx.canvas.height = height;
    this._backFrameCtx.canvas.width = width;
    this._backFrameCtx.canvas.height = height;
    this._tmpCtx.canvas.width = width;
    this._tmpCtx.canvas.height = height;
  }

  /*-------------------------------- データ構造 --------------------------------*/
  get frameIdx() {
    return this._frameIdx;
  }
  get frameSz() {
    return this._frameDataList.length;
  }
  get layerIdx() {
    return this._layerIdx;
  }
  _getLayerDataList(frameIdx) {
    return this._frameDataList[frameIdx].layerDataList;
  }
  getLayerDataListSz(frameIdx) {
    return this._getLayerDataList(frameIdx).length;
  }
  _createLayerData() {
    return {
      compactImage: new CompactImage(this),
    };
  }
  _cloneLayerData(frameIdx, layerIdx) {
    const layerData = {...this._frameDataList[frameIdx].layerDataList[layerIdx]};
    layerData.compactImage = layerData.compactImage.clone();
    return layerData;
  }
  _createFrameData() {
    return {
      layerDataList: [],
      mergedCtx: new OffscreenCanvas(this.width, this.height).getContext('2d'),
    };
  }
  _cloneFrameData(frameIdx) {
    const src = this._frameDataList[frameIdx];
    const dst = this._createFrameData();
    for(let i = 0; i < src.layerDataList.length; ++i) {
      dst.layerDataList.push(this._cloneLayerData(frameIdx, i));
    }
    dst.mergedCtx.drawImage(src.mergedCtx.canvas, 0, 0);
    return dst;
  }

  /*-------------------------------- フレーム操作 --------------------------------*/
  addFrame(frameIdx, sz) {
    const frameDataList = [];
    for(let i = 0; i < sz; ++i) {
      const frameData = this._createFrameData();
      frameData.layerDataList.push(this._createLayerData());
      frameDataList.push(frameData);
    }
    this._frameDataList.splice(frameIdx, 0, ...frameDataList);
    if(frameIdx <= this._frameIdx) this._frameIdx += sz;
  }
  insertFrame(frameIdx, ...frameDataList) {
    this._frameDataList.splice(frameIdx, 0, ...frameDataList);
    if(frameIdx <= this._frameIdx) this._frameIdx += frameDataList.length;
  }
  duplicateFrame(frameIdx, sz) {
    //this._updateFrameMergedCanvas(this._frameIdx);
    const frameDataList = [];
    for(let i = 0; i < sz; ++i) {
      frameDataList.push(this._cloneFrameData(frameIdx + i));
    }
    this._frameDataList.splice(frameIdx, 0, ...frameDataList);
    if(frameIdx <= this._frameIdx) this._frameIdx += sz;
  }
  deleteFrame(frameIdx, sz) {
    return this._frameDataList.splice(frameIdx, sz);
  }
  moveFrame(srcIdx, srcSz, dstIdx) {
    const arrSrc = this._frameDataList.slice(srcIdx, srcIdx + srcSz);
    this._frameDataList.splice(dstIdx, 0, ...arrSrc);
    this.deleteFrame(srcIdx < dstIdx ? srcIdx : srcIdx + srcSz, srcSz);
  }

  /*-------------------------------- レイヤー操作 --------------------------------*/
  addLayer(layerIdx, sz) {
    const layerDataList = [];
    for(let i = 0; i < sz; ++i) {
      layerDataList.push(this._createLayerData());
    }
    this._getLayerDataList(this._frameIdx).splice(layerIdx, 0, ...layerDataList);
    if(layerIdx <= this._layerIdx) this._layerIdx += sz;
  }
  insertLayer(layerIdx, ...layerDataList) {
    this._getLayerDataList(this._frameIdx).splice(layerIdx, 0, ...layerDataList);
    for(let i = 0; i < layerDataList.length; ++i) {
      this._onUpdateLayer(this._frameIdx, layerIdx + i);
    }
    this._onUpdateFrame(this._frameIdx);
  }
  duplicateLayer(layerIdx, sz) {
    const layerDataList = [];
    for(let i = 0; i < sz; ++i) {
      layerDataList.push(this._cloneLayerData(this._frameIdx, layerIdx + i));
    }
    this._getLayerDataList(this._frameIdx).splice(layerIdx, 0, ...layerDataList);
  }
  // 削除されたデータを配列で返す
  deleteLayer(layerIdx, sz) {
    const deleted = this._getLayerDataList(this._frameIdx).splice(layerIdx, sz);
    this._onUpdateFrame(this._frameIdx);
    return deleted;
  }
  // 統合に使用されたデータを配列で返す
  mergeLayer(layerIdx, sz) {
    const layerData = this._createLayerData();
    layerData.compactImage.merge(...this._getLayerDataList(this._frameIdx).slice(layerIdx, layerIdx + sz).map(v => v.compactImage));

    this._getLayerDataList(this._frameIdx).splice(layerIdx, 0, layerData);
    const deleted =  this.deleteLayer(layerIdx + 1, sz);
    this._onUpdateLayer(this._frameIdx, layerIdx);
    this._onUpdateFrame(this._frameIdx);
    return deleted;
  }
  moveLayer(srcIdx, srcSz, dstIdx) {
    const arrSrc = this._getLayerDataList(this._frameIdx).slice(srcIdx, srcIdx + srcSz);
    this._getLayerDataList(this._frameIdx).splice(dstIdx, 0, ...arrSrc);
    this.deleteLayer(srcIdx < dstIdx ? srcIdx : srcIdx + srcSz, srcSz);
    this._onUpdateFrame(this._frameIdx);
  }

  /*-------------------------------- 選択 --------------------------------*/
  //--- 描画の際に通常表示するフレームを指定　その手前と奥にある画像をそれぞれ１つにまとめる
  selectFrame(frameIdx, layerIdx = 0) {
    console.assert(0 <= frameIdx && frameIdx < this._frameDataList.length);
    //this._updateFrameMergedCanvas(this._frameIdx);
    this._frameIdx = frameIdx;

    //-- クリア
    this._frontFrameCtx.clearRect(0, 0, this.width, this.height);
    this._backFrameCtx.clearRect(0, 0, this.width, this.height);

    //-- 手前のフレームを統合して前景キャンバスに描画
    for(let i = frameIdx - 1; 0 <= i; --i) {
      const gap = frameIdx - i;
      const alpha = 255 / gap;
      if(1 <= alpha) this._frontFrameCtx.drawImage(this._frameDataList[i].mergedCtx.canvas, 0, 0);
    }

    //-- 奥のフレームを統合して背景キャンバスに描画
    for(let i = this._frameDataList.length - 1; frameIdx < i; --i) {
      const gap = i - frameIdx;
      const alpha = 255 / gap;
      if(1 <= alpha) this._backFrameCtx.drawImage(this._frameDataList[i].mergedCtx.canvas, 0, 0);
    }

    this.selectLayer(layerIdx);
    this._updatePreview();
  }
  
  //--- 描画の際に通常表示するレイヤーを指定　その手前と奥にある画像をそれぞれ１つにまとめる
  selectLayer(layerIdx) {
    console.assert(0 <= layerIdx && layerIdx < this._getLayerDataList(this._frameIdx).length);
    this._layerIdx = layerIdx;

    //-- クリア
    this._frontLayerCtx.clearRect(0, 0, this.width, this.height);
    this._backLayerCtx.clearRect(0, 0, this.width, this.height);

    //-- 手前のレイヤーを統合して前景キャンバスに描画
    for(let i = layerIdx - 1; 0 <= i; --i) {
      this._getLayerDataList(this._frameIdx)[i].compactImage.putImageData(this._tmpCtx);
      this._frontLayerCtx.drawImage(this._tmpCtx.canvas, 0, 0);
    }

    //-- 奥のレイヤーを統合して背景キャンバスに描画
    for(let i = this._getLayerDataList(this._frameIdx).length - 1; layerIdx < i; --i) {
      this._getLayerDataList(this._frameIdx)[i].compactImage.putImageData(this._tmpCtx);
      this._backLayerCtx.drawImage(this._tmpCtx.canvas, 0, 0);
    }
  }

  //--- 再選択して再描画
  reselect() {
    this.selectFrame(this._frameIdx, this._layerIdx);
  }

  //--- フレーム毎に属するレイヤーを用意されたキャンバスに統合して描画
  _updateFrameMergedCanvas(frameIdx) {
    if(0 <= frameIdx && frameIdx < this._frameDataList.length) {
      const {layerDataList, mergedCtx} = this._frameDataList[frameIdx];
      mergedCtx.canvas.width = this.width;
      mergedCtx.canvas.height = this.height;
      for(let i = layerDataList.length - 1; 0 <= i; --i) {
        const layerData = layerDataList[i];
        mergedCtx.drawImage(layerData.compactImage.getAsOffscreenCanvas(), 0, 0);
      }
    }
  }
  _updateFrameMergedCanvasAll() {
    for(let i = 0; i < this._frameDataList.length; ++i) {
      this._updateFrameMergedCanvas(i);
    }
  }

  //--- 外部のcanvasを更新
  _onUpdateLayer(frameIdx, layerIdx) {
    if(frameIdx === this._frameIdx) this._updateLayerThumbnail(layerIdx);
  }
  _onUpdateFrame(frameIdx) {
    this._updateFrameMergedCanvas(frameIdx);
    this._updateFrameThumbnail(frameIdx);
    if(frameIdx === this._frameIdx) this._updatePreview();
  }

  /*-------------------------------- 保留レイヤー --------------------------------*/
  //--- 指定IDの画像をコピーした保留レイヤー
  preparePending() {
    this._pending = this._getLayerDataList(this._frameIdx)[this._layerIdx].compactImage.createImageData();
    this._pendingTarget = 0;
    this._prevPixel = this._prev2Pixel = null;
  }
  resetPending() {
    this._pending = this._getLayerDataList(this._frameIdx)[this._layerIdx].compactImage.createImageData();
  }

  //--- ペンツール用保留レイヤー
  preparePendingPen(pixelColor) {
    const image = this._getLayerDataList(this._frameIdx)[this._layerIdx].compactImage.createImageData();
    this._pending = new PenDrawableImage(this.width, this.height, image, pixelColor);
    this._pendingTarget = 0;
  }

  //--- 保留レイヤーをレイヤーに上書き　差分をコールバックに渡す
  confirmPending(callback) {
    const compactImage = this._getLayerDataList(this._frameIdx)[this._layerIdx].compactImage;
    const pixels = [];
    const oldPixelColors = [];
    
    const image = compactImage.createImageData();
    for(let i = 0; i < this._pending.data.length; i += 4) {
      const newPixelColor = this._pending.data.subarray(i, i + 4);
      const oldPixelColor = compactImage.getPixelIdx(i);
      if(!Edit.isSameColor(newPixelColor, 0, oldPixelColor, 0)) {
        image.data.set(newPixelColor[3] === 0 ? Edit.ZERO_PIXEL : newPixelColor, i);
        pixels.push(i);
        oldPixelColors.push(oldPixelColor);
      }
    }
    compactImage.setImageData(image);

    this._pending = null;
    if(0 < pixels.length) {
      callback(this._frameIdx, this._layerIdx, pixels, oldPixelColors);
      this._onUpdateLayer(this._frameIdx, this._layerIdx);
      this._onUpdateFrame(this._frameIdx);
    }
  }

  //--- 保留レイヤーを破棄
  cancelPending() {
    this._pending = null;
  }

  /*-------------------------------- 保留レイヤー編集 --------------------------------*/
  //--- 指定された中心座標と太さから画像上の範囲を計算する
  getThickRect(centerX, centerY, thick) {
    return Edit.getThickRect(centerX, centerY, thick, this.width, this.height);
  }

  //--- ペンツールで描画
  drawPendingPen(x1, y1, x2, y2, thick, pixelPerfect) {
    console.assert(this._pending instanceof PenDrawableImage);
    this._pending.line(x1, y1, x2, y2, thick, pixelPerfect);
  }

  //--- 線を描画
  drawPendingLine(pixelColor, x1, y1, x2, y2, thick) {
    Edit.drawLine(this._pending, pixelColor, x1, y1, x2, y2, thick);
  }

  //--- 曲線を描画
  drawPendingCurve(pixelColor, x1, y1, cpx, cpy, x2, y2, thick) {
    Edit.drawCurve(this._pending, pixelColor, x1, y1, cpx, cpy, x2, y2, thick);


    // const canvas = new OffscreenCanvas(this._width, this._height);
    // const ctx = canvas.getContext('2d');
    // ctx.imageSmoothingEnabled = false;
    // ctx.putImageData(this._pending, 0, 0);
    // ctx.beginPath();
    // ctx.moveTo(x1, y1);
    // ctx.quadraticCurveTo(cpx, cpy, x2, y2);

    // const color = pixelColor.slice();
    // color[3] = color[3] / 255;
    // ctx.strokeStyle = `rgba(${color.join(',')})`;
    // ctx.lineWidth = thick;
    // ctx.stroke();

    // const image = ctx.getImageData(0, 0, this._width, this._height);
    // this._pending.data.set(image.data, 0, 0);
  }

  //--- 塗りつぶし
  fillPending(pixelColor, x, y, includeDiagonals, distRGB = 0, distA = 0) {
    Edit.fill(this._pending, pixelColor, x, y, includeDiagonals, distRGB, distA)
  }

  //--- フチ取り／残し塗りつぶし
  fillPendingEdge(pixelColor, x, y, arrowKeys, leave, includeDiagonals, distRGB = 0, distA = 0) {
    Edit.fillEdge(this._pending, pixelColor, x, y, arrowKeys, leave, includeDiagonals, distRGB, distA);
  }

  //--- ジャギ取り
  erasePendingJaggies(x, y, distRGB = 0, distA = 0) {
    Edit.eraseJaggies(this._pending, x, y, distRGB, distA);
  }

  //--- 色置換
  replaceColorPending(dstColor, srcColor, distRGB = 0, distA = 0) {
    console.assert(this._pending !== null);
    Edit.replaceColor(this._pending, dstColor, srcColor, distRGB, distA);
  }

  //--- 矩形を描画
  drawPendingRect(pixelColor, x1, y1, x2, y2, thick, fill) {
    Edit.drawRect(this._pending, pixelColor, x1, y1, x2, y2, thick, fill);
  }

  //--- 楕円を描画
  drawPendingCircle(pixelColor, x1, y1, x2, y2, thick, fill) {
    Edit.drawCircle(this._pending, pixelColor, x1, y1, x2, y2, thick, fill);
  }

  //--- 指定ピクセル上の色を取得　スポイトツールで使用
  getPixelColor(x, y) {
    [x, y] = [x, y].map(Math.floor);
    const compactImage = this._getLayerDataList(this._frameIdx)[this._layerIdx].compactImage;
    if(0 <= x && x < this.width && 0 <= y && y < this.height) {
      return Array.from(compactImage.getPixel(x, y));
    } else {
      return [0, 0, 0, 0];
    }
  }

  /*-------------------------------- 直接画像編集 --------------------------------*/
  //--- 全フレームの全レイヤーを色置換
  replaceColorInAllFrames(dstColor, srcColor, distRGB, distA) {
    const oldCompactImageLists = [];
    let byteSizeSum = 0;
    for(let i = 0; i < this.frameSz; ++i) {
      const res = this.replaceColorInFrame(i, dstColor, srcColor, distRGB, distA);
      if(res) {
        const [oldCompactImages, byteSize] = res;
        oldCompactImageLists.push(oldCompactImages);
        byteSizeSum += byteSize;
      } else {
        oldCompactImageLists.push(null);
      }
    }
    return byteSizeSum ? [oldCompactImageLists, byteSizeSum] : null;
  }

  //--- 指定フレーム内の全レイヤーを色置換
  replaceColorInFrame(frameIdx, dstColor, srcColor, distRGB, distA) {
    console.assert(this._pending === null);
    if(Edit.isSameColor(srcColor, 0, dstColor, 0)) return null;
    const oldCompactImages = [];
    const layerDataList = this._getLayerDataList(frameIdx)
    let byteSize = 0;
    for(let i = 0; i < layerDataList.length; ++i) {
      const compactImage = layerDataList[i].compactImage;
      const clone = compactImage.clone();
      
      const image = compactImage.createImageData();
      let flag = false;
      for(let j = 0; j < image.data.length; j += 4) {
        if(
          Edit.isSimilarColor(image.data, j, srcColor, 0, distRGB, distA) &&
          !Edit.isSameColor(image.data, j, dstColor, 0)
        ) {
          image.data.set(dstColor, j);
          flag = true;
        }
      }
      compactImage.setImageData(image);

      if(flag) {
        oldCompactImages[i] = clone;
        byteSize += clone.byteSize();
        this._onUpdateLayer(frameIdx, i);
      }
    }
    this._onUpdateFrame(frameIdx);
    return byteSize ? [oldCompactImages, byteSize] : null;
  }

  /*-------------------------------- 復元用 --------------------------------*/
  //--- ピクセルを１つづつ置換
  putPixelColor(layerIdx, pixelColor, pixels) {
    const compactImage = this._frameDataList[this._frameIdx].layerDataList[layerIdx].compactImage;
    const image = compactImage.createImageData();
    for(let i = 0; i < pixels.length; ++i) {
      image.data.set(pixelColor, pixels[i]);
    }
    compactImage.setImageData(image);
    this._onUpdateLayer(this._frameIdx, layerIdx);
    this._onUpdateFrame(this._frameIdx);
  }
  putPixelColors(layerIdx, pixelColors, pixels) {
    const compactImage = this._frameDataList[this._frameIdx].layerDataList[layerIdx].compactImage;
    const image = compactImage.createImageData();
    for(let i = 0; i < pixels.length; ++i) {
      image.data.set(pixelColors[i], pixels[i]);
    }
    compactImage.setImageData(image);
    this._onUpdateLayer(this._frameIdx, layerIdx);
    this._onUpdateFrame(this._frameIdx);
  }

  //--- compactImageを直接置換
  replaceCompactImage(frameIdx, layerIdx, compactImage) {
    this._frameDataList[frameIdx].layerDataList[layerIdx].compactImage = compactImage.clone();
    this._onUpdateLayer(frameIdx, layerIdx);
    this._onUpdateFrame(frameIdx);
  }
  replaceCompactImages(frameIdx, compactImages) {
    console.assert(compactImages.length === this._getLayerDataList(frameIdx).length);
    for(let i = 0; i < compactImages.length; ++i) {
      if(compactImages[i]) {
        this.replaceCompactImage(frameIdx, i, compactImages[i]);
      }
    }
  }
  replaceCompactImageLists(compactImageLists) {
    console.assert(compactImageLists.length === this.frameSz);
    for(let i = 0; i < compactImageLists.length; ++i) {
      if(compactImageLists[i]) {
        this.replaceCompactImages(i, compactImageLists[i]);
      }
    }
  }

  /*-------------------------------- 選択ツール用保留レイヤー --------------------------------*/
  // target=0:中央のみ,1:選択フレーム,2:全フレーム
  prepareSelection(tgtX, tgtY, tgtW, tgtH, lassoMask, target) {
    this._pending = (() => {
      const ctx = new OffscreenCanvas(this.width, this.height).getContext('2d');
      this.draw1x(ctx, target);
      return ctx.getImageData(0, 0, this.width, this.height);
    })();
    this._pendingTarget = target;
    this._selectionProcessor = new SelectionProcessor;
    this._selectionProcessor.initWithImageSplitting(this._pending, tgtX, tgtY, tgtW, tgtH, lassoMask, ImageDataManager._selectionTransformAlgo);
    this._isPasted = false;
  }
  confirmSelection(clear) {
    const func = (changes, frameIdx, layerIdx) => {
      const compactImage = this._getLayerDataList(frameIdx)[layerIdx].compactImage;
      const pending = compactImage.createImageData();
      const selectionProcessor = new SelectionProcessor;
      selectionProcessor.initWithAnother(pending, this._selectionProcessor, this._isPasted, clear);
      const {image, ofsX, ofsY} = selectionProcessor.getSelectionImage();
      Edit.pasteImage(pending, image, ofsX, ofsY, (dstPixel, srcPixel) => {
        dstPixel.set(srcPixel);
      });
      const newImage = compactImage.createImageData();
      const data = newImage.data;
      let orgImageClone = null;
      data.forEach((v, i) => {
        const w = pending.data[i];
        if(v !== w) {
          if(!orgImageClone) {
            orgImageClone = this._getLayerDataList(frameIdx)[layerIdx].compactImage.clone();
          }
          data[i] = w;
        }
      });
      compactImage.setImageData(newImage);
      const newImageClone = this._getLayerDataList(frameIdx)[layerIdx].compactImage.clone();
      if(orgImageClone) {
        this._onUpdateLayer(frameIdx, layerIdx);
        this._onUpdateFrame(frameIdx);
        changes.push({frameIdx, layerIdx, orgImage: orgImageClone, newImage: newImageClone});
        return orgImageClone.byteSize() + newImageClone.byteSize();
      } else {
        return 0;
      }
    }

    const changes = [];
    let byteSize = 0;
    switch(this._pendingTarget) {
      case 0:
        byteSize += func(changes, this._frameIdx, this._layerIdx);
        break;
      case 1:
        for(let layerIdx = 0; layerIdx < this._getLayerDataList(this._frameIdx).length; ++layerIdx) {
          byteSize += func(changes, this._frameIdx, layerIdx);
        }
        this._updateFrameMergedCanvas(this._frameIdx);
        this.reselect();
        break;
      case 2:
        for(let frameIdx = 0; frameIdx < this._frameDataList.length; ++frameIdx) {
          for(let layerIdx = 0; layerIdx < this._getLayerDataList(frameIdx).length; ++layerIdx) {
            byteSize += func(changes, frameIdx, layerIdx);
          }
          this._updateFrameMergedCanvas(frameIdx);
        }
        this.reselect();
        break;
    }
    this.cancelSelection();
    return {changes, byteSize};
  }
  cancelSelection() {
    this._initSelection();
    this.cancelPending();
  }
  _initSelection() {
    this._selectionProcessor = null;
  }
  /*---------------- 選択範囲操作 ----------------*/
  moveSelection(x, y) {
    this._selectionProcessor.moveSelection(x, y);
  }
  resizeSelection(x, y, w, h) {
    this._selectionProcessor.resizeSelection(x, y, w, h);
  }
  rotateSelection(rad) {
    this._selectionProcessor.rotateSelection(rad)
  }
  flipSelectionX() {
    this._selectionProcessor.flipSelectionX();
  }
  flipSelectionY() {
    this._selectionProcessor.flipSelectionY();
  }
  /*---------------- コピペ ----------------*/
  copySelectionToClipboard(onCopy) {
    const {image, ofsX, ofsY, width, height} = this._selectionProcessor.getSelectionImage();
    ImageDataManager._lastCopiedRect = [ofsX, ofsY, width, height];
    const ctx = new OffscreenCanvas(width, height).getContext('2d');
    ctx.putImageData(image, 0, 0);
    ctx.canvas.convertToBlob().then(blob => {
      if(this.clipboardPermitted) {
        navigator.clipboard.write([
          new ClipboardItem({[blob.type]: blob}),
        ]).then(() => {
          onCopy(ImageDataManager._lastCopiedRect);
        });
      } else {
        ImageDataManager._pseudoClipboard = blob;
        onCopy(ImageDataManager._lastCopiedRect);
      }
    });
  }
  async getBlobFromClipboard(e) {
    if(this.clipboardPermitted) {
      if(e) {
        for(const item of e.clipboardData.items) {
          if(item.type.indexOf('image/') === 0) {
            return item.getAsFile();
          }
        }
      } else {
        const items = await navigator.clipboard.read();
        for(const item of items) {
          for(const type of item.types) {
            if(type.indexOf('image/') === 0) {
              return await item.getType(type);
            }
          }
        }
      }
      return null;
    } else {
      return ImageDataManager._pseudoClipboard;
    }
  }
  async prepareSelectionFromBlob(blob) {
    this.preparePending();
    this._isPasted = true;
    this._selectionProcessor = new SelectionProcessor;
    return this._selectionProcessor.initWithBlob(blob, ImageDataManager._lastCopiedRect, ImageDataManager._selectionTransformAlgo, this.width, this.height);
  }
  /*---------------- 選択範囲整形 ----------------*/
  setSelectionTransformAlgo(algoName) {
    ImageDataManager._selectionTransformAlgo = algoName;
    if(this._selectionProcessor) this._selectionProcessor.prepareSelectionTransformAlgo(algoName);
  }

  /*-------------------------------- 描画 --------------------------------*/
  //--- imageDataをDragCameraを通して描画
  /*static _drawImageData(image, ctx, dragCamera) {
    const {width, height} = ctx.canvas;
    const [wx1, wy1] = dragCamera.localPtToWorldPt(0, 0);
    const [wx2, wy2] = dragCamera.localPtToWorldPt(width, height);
    const x1 = Math.max(0, Math.floor(wx1));
    const x2 = Math.min(Math.ceil(wx2), image.width - 1);
    const y1 = Math.max(0, Math.floor(wy1));
    const y2 = Math.min(Math.ceil(wy2), image.height - 1);
    const data = image.data;
    
    for(let y = y1; y <= y2; ++y) {
      for(let x = x1; x <= x2; ++x) {
        const layerIdx = (y * image.width + x) * 4;
        ctx.fillStyle = `rgba(${data[layerIdx]},${data[layerIdx + 1]},${data[layerIdx + 2]},${data[layerIdx + 3]/255})`;
        const [lx1, ly1] = dragCamera.worldPtToLocalPt(x, y);
        const [lx2, ly2] = dragCamera.worldPtToLocalPt(x + 1, y + 1);
        ctx.fillRect(lx1, ly1, lx2 - lx1, ly2 - ly1);
      }
    }
  }*/

  //--- canvas（や使う予定はないけどimg）要素をDragCameraを通して描画
  static _drawElement(element, ctx, dragCamera) {
    const canvas = ctx.canvas;
    const [wx1, wy1] = dragCamera.localPtToWorldPt(0, 0);
    const [wx2, wy2] = dragCamera.localPtToWorldPt(canvas.width, canvas.height);
    const sx = Math.max(0, wx1);
    const sx2 = Math.min(wx2, element.width);
    const sy = Math.max(0, wy1);
    const sy2 = Math.min(wy2, element.height);
    const sw = sx2 - sx;
    const sh = sy2 - sy;
    const [dx, dy, dw, dh] = dragCamera.worldPtSzToLocalPtSz(sx, sy, sw, sh);
    ctx.drawImage(element, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  //--- DragCameraを通して描画
  draw(ctx, dragCamera) {
    if(ImageDataManager.canvasBackground) {
      //const style = getComputedStyle(document.documentElement);
      //const canvas = ctx.canvas;
      const ptSz = dragCamera.worldPtSzToLocalPtSzD(0, 0, this.width, this.height);

      //-- 画像サイズ外の靄（もや）
      /*ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(...ptSz);
      ctx.clip('evenodd');
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();*/

      //-- 画像サイズ内の靄（もや）
      ctx.save();
      ctx.fillStyle = ImageDataManager.canvasBackground;
      ctx.fillRect(...ptSz);
      ctx.restore();
    }

    //-- フラグ
    const layerDrawing = this._pending === null || this._pendingTarget < 1;
    const frameDrawing = this._pending === null || this._pendingTarget < 2;

    //-- 背景
    if(frameDrawing && ImageDataManager.backFrameOpacity) {
      ctx.globalAlpha = ImageDataManager.backFrameOpacity;
      ImageDataManager._drawElement(this._backFrameCtx.canvas, ctx, dragCamera);
    }
    if(layerDrawing && ImageDataManager.backLayerOpacity) {
      ctx.globalAlpha = ImageDataManager.backLayerOpacity;
      ImageDataManager._drawElement(this._backLayerCtx.canvas, ctx, dragCamera);
    }
    ctx.globalAlpha = 1;
    
    //-- 中央
    const layerData = this._getLayerDataList(this._frameIdx)[this._layerIdx];
    if(this._pending === null) {
      layerData.compactImage.putImageData(this._tmpCtx);
      //ImageDataManager._drawImageData(layerData.compactImage.createImageData(), ctx, dragCamera);
    } else {
      this._tmpCtx.putImageData(this._pending, 0, 0);
    }
    ImageDataManager._drawElement(this._tmpCtx.canvas, ctx, dragCamera);

    //-- 選択ツール用保留レイヤー
    if(this._selectionProcessor) {
      const {image, ofsX, ofsY} = this._selectionProcessor.getSelectionImage();
      const canvas = new OffscreenCanvas(this.width, this.height);
      canvas.getContext('2d').putImageData(image, ofsX, ofsY);
      ImageDataManager._drawElement(canvas, ctx, dragCamera);
    }

    //-- 前景
    if(layerDrawing && ImageDataManager.frontLayerOpacity) {
      ctx.globalAlpha = ImageDataManager.frontLayerOpacity;
      ImageDataManager._drawElement(this._frontLayerCtx.canvas, ctx, dragCamera);
    }
    if(frameDrawing && ImageDataManager.frontFrameOpacity) {
      ctx.globalAlpha = ImageDataManager.frontFrameOpacity;
      ImageDataManager._drawElement(this._frontFrameCtx.canvas, ctx, dragCamera);
    }
    ctx.globalAlpha = 1;
  }

  //--- DragCameraを通さず描画
  draw1x(ctx, target) {
    //-- フラグ
    const layerDrawing = target >= 1;
    const frameDrawing = target >= 2;

    //-- 背景
    if(frameDrawing && ImageDataManager.backFrameOpacity) {
      ctx.globalAlpha = ImageDataManager.backFrameOpacity;
      ctx.drawImage(this._backFrameCtx.canvas, 0, 0);
    }
    if(layerDrawing && ImageDataManager.backLayerOpacity) {
      ctx.globalAlpha = ImageDataManager.backLayerOpacity;
      ctx.drawImage(this._backLayerCtx.canvas, 0, 0);
    }
    ctx.globalAlpha = 1;
    
    //-- 中央
    const layerData = this._getLayerDataList(this._frameIdx)[this._layerIdx];
    layerData.compactImage.putImageData(this._tmpCtx);
    ctx.drawImage(this._tmpCtx.canvas, 0, 0);

    //-- 前景
    if(layerDrawing && ImageDataManager.frontLayerOpacity) {
      ctx.globalAlpha = ImageDataManager.frontLayerOpacity;
      ctx.drawImage(this._frontLayerCtx.canvas, 0, 0);
    }
    if(frameDrawing && ImageDataManager.frontFrameOpacity) {
      ctx.globalAlpha = ImageDataManager.frontFrameOpacity;
      ctx.drawImage(this._frontFrameCtx.canvas, 0, 0);
    }
    ctx.globalAlpha = 1;
  }

  //--- サムネイルを描画
  drawLayerThumbnail(canvas, layerIdx) {
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext('2d');
    this._getLayerDataList(this._frameIdx)[layerIdx].compactImage.putImageData(ctx);
  }
  drawFrameThumbnail(canvas, frameIdx) {
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(this._frameDataList[frameIdx].mergedCtx.canvas, 0, 0);
  }
  drawPreview(canvas, frameIdx, shadow, resizeOnly) {
    const {width, height} = this;
    if(canvas.width !== width) canvas.width = width;
    if(canvas.height !== height) canvas.height = height;
    if(resizeOnly) return;
    
    const ctx = canvas.getContext('2d');
    ctx.save();
    if(shadow) {
      ctx.shadowColor = 'rgba(0,0,0,.5)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
    }
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(this._frameDataList[frameIdx].mergedCtx.canvas, 0, 0);
    ctx.restore();
  }

  /*-------------------------------- ダウンロード --------------------------------*/
  exportSinglePNG(scale, onReady) {
    this._updateFrameMergedCanvas(this._frameIdx);
    const {width, height} = this;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    
    const image = this._frameDataList[this._frameIdx].mergedCtx.canvas;
    ctx.drawImage(image, 0, 0, width * scale, height * scale);

    onReady(ctx.canvas.toDataURL());
  }
  exportConcatenatedPNG(scale, onReady) {
    this._updateFrameMergedCanvas(this._frameIdx);
    const {width, height, frameSz} = this;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale * frameSz;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    for(let i = 0; i < frameSz; ++i) {
      const x = width * scale * i;
      const image = this._frameDataList[i].mergedCtx.canvas;
      ctx.drawImage(image, x, 0, width * scale, height * scale);
    }
    onReady(ctx.canvas.toDataURL());
  }
  exportGIF(scale, delay, onReady) {
    //this._updateFrameMergedCanvas(this._frameIdx);
    const width = this.width * scale;
    const height = this.height * scale;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    import('gif-transparency').then(({default: Animated_GIF}) => {
      const ag = new Animated_GIF({
        disposal: 2,
      });
      ag.setSize(width, height);
      ag.setDelay(delay);
      ctx.imageSmoothingEnabled = false;
      for(let i = 0; i < this.frameSz; ++i) {
        const image = this._frameDataList[i].mergedCtx.canvas;
        ctx.clearRect(0, 0, width, height)
        ctx.drawImage(image, 0, 0, width, height);
        ag.addFrame(canvas);
      }
      ag.getBase64GIF(onReady);
    });
  }
  getImages() {
    const {width, height} = this;
    return this._frameDataList.map(frameData => frameData.mergedCtx.getImageData(0, 0, width, height));
  }
  async copySelectedFrameToClipboard(scale) {
    //this._updateFrameMergedCanvas(this._frameIdx);
    const [width, height] = [this.width * scale, this.height * scale];
    const src = this._frameDataList[this._frameIdx].mergedCtx.canvas;
    const dst = new OffscreenCanvas(width, height);
    const dstCtx = dst.getContext('2d');
    dstCtx.imageSmoothingEnabled = false;
    dstCtx.drawImage(src, 0, 0, width, height);
    return dst.convertToBlob().then(blob => {
      navigator.clipboard.write([
        new ClipboardItem({[blob.type]: blob}),
      ]);
    });
  }
  save() {
    return {
      width: this.width,
      height: this.height,
      frames: this._frameDataList.map(v => v.layerDataList.map(
        w => w.compactImage.createImageData()
      )),
    };
  }
  restore({width, height, frames}) {
    this._width = width;
    this._height = height;
    this._resizeCanvases(width, height);
    this._frameDataList = [];
    for(const layers of frames) {
      const frameData = this._createFrameData();
      this._frameDataList.push(frameData);
      for(const layer of layers) {
        const layerData = this._createLayerData();
        frameData.layerDataList.push(layerData);
        layerData.compactImage.setImageData(layer);
      }
    }
    this._updateFrameMergedCanvasAll();
    //this._onResize(width, height);
  }
}
